<template>
  <div class="flex flex-col items-center gap-6 py-8 px-4 text-center">
    <em class="mdi text-6xl" :class="iconClass" />

    <div class="text-lg font-medium">
      {{ title }}
    </div>

    <template v-if="state.phase === 'available'">
      <div class="text-sm text-gray-600 dark:text-gray-300">
        <p>
          Bevor Sie fortfahren können, muss die Q-Tool-Firmware aktualisiert
          werden.
        </p>
        <p class="mt-2">
          <span class="font-medium">Aktuell:</span>
          {{ state.currentVersion || 'unbekannt' }}
          <span class="mx-2">→</span>
          <span class="font-medium">Ziel:</span>
          {{ state.targetVersion || 'unbekannt' }}
        </p>
      </div>
      <button
        class="rounded-lg bg-blue-500 px-6 py-3 text-white font-medium disabled:opacity-50"
        @click="emit('start')"
      >
        Aktualisieren
      </button>
    </template>

    <template v-else-if="state.phase === 'preparing'">
      <div class="text-sm text-gray-600 dark:text-gray-300">
        Update wird vorbereitet — Q-Tool bitte nicht trennen.
      </div>
    </template>

    <template v-else-if="state.phase === 'updating'">
      <div class="w-full max-w-xs">
        <div class="h-2 w-full rounded-full bg-gray-200 dark:bg-dark-3">
          <div
            class="h-2 rounded-full bg-blue-500 transition-all"
            :style="{ width: `${state.percent}%` }"
          />
        </div>
        <div class="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {{ state.percent }}%
        </div>
      </div>
      <div class="text-sm text-gray-600 dark:text-gray-300">
        Q-Tool wird aktualisiert — bitte Gerät nicht trennen.
      </div>
    </template>

    <template v-else-if="state.phase === 'complete'">
      <div class="text-sm text-gray-600 dark:text-gray-300 space-y-2">
        <p>Firmware-Update erfolgreich abgeschlossen.</p>
        <p>
          Das Q-Tool startet jetzt neu. Bitte warten Sie, bis es sich wieder
          einschaltet, und verbinden Sie das Q-Tool anschließend erneut.
        </p>
      </div>
      <button
        class="flex items-center gap-2 rounded-lg bg-blue-500 px-6 py-3 text-white font-medium disabled:opacity-60"
        :disabled="dismissing"
        @click="emit('dismiss')"
      >
        <em v-if="dismissing" class="mdi mdi-loading animate-spin" />
        <span>{{
          dismissing ? 'Q-Tool startet neu…' : 'Q-Tool erneut verbinden'
        }}</span>
      </button>
    </template>

    <template v-else-if="state.phase === 'error'">
      <div class="text-sm text-red-600 dark:text-red-400">
        {{ errorCopy }}
      </div>
      <div class="flex gap-2">
        <button
          v-if="state.reason !== 'UPDATE_NOT_POSSIBLE'"
          class="rounded-lg bg-blue-500 px-4 py-2 text-sm text-white font-medium"
          @click="emit('start')"
        >
          Erneut versuchen
        </button>
        <button
          class="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-dark-3"
          @click="emit('dismiss')"
        >
          Schließen
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { FirmwareUpdatePhase } from '@/composables/useQToolFirmwareUpdate';
import type { QToolFirmwareUpdateErrorReason } from '@/native/plugins/QTool/definitions';

const props = defineProps<{
  state: FirmwareUpdatePhase;
  dismissing?: boolean;
}>();

const emit = defineEmits<{
  start: [];
  dismiss: [];
}>();

const iconClass = computed(() => {
  switch (props.state.phase) {
    case 'available':
      return 'mdi-cloud-download text-blue-500';
    case 'preparing':
    case 'updating':
      return 'mdi-loading animate-spin text-blue-500';
    case 'complete':
      return 'mdi-check-circle text-green-500';
    case 'error':
      return 'mdi-alert-circle text-red-500';
    default:
      return 'mdi-cloud-download text-blue-500';
  }
});

const title = computed(() => {
  switch (props.state.phase) {
    case 'available':
      return 'Firmware-Update verfügbar';
    case 'preparing':
      return 'Update wird vorbereitet';
    case 'updating':
      return 'Firmware wird aktualisiert';
    case 'complete':
      return 'Update abgeschlossen';
    case 'error':
      return 'Update fehlgeschlagen';
    default:
      return '';
  }
});

// Total over the union on purpose: a reason added in definitions.ts without
// copy here is a compile error, not a silent "Unbekannter Fehler" shown for a
// condition the installer could have acted on (PHX-2407).
const ERROR_COPY: Record<QToolFirmwareUpdateErrorReason, string> = {
  PREPARE_FAILED:
    'Update konnte nicht vorbereitet werden. Bitte erneut versuchen.',
  BLOCK_FAILED: 'Datenübertragung fehlgeschlagen. Bitte erneut versuchen.',
  UPDATE_FAILED: 'Update fehlgeschlagen. Bitte erneut versuchen.',
  UPDATE_NOT_POSSIBLE:
    'Update ist auf diesem Q-Tool nicht möglich. Bitte Support kontaktieren.',
  STALLED:
    'Das Update wurde abgebrochen, weil das Q-Tool nicht mehr geantwortet hat. Bitte Q-Tool und Bluetooth prüfen und erneut versuchen.',
  DEADLINE_EXCEEDED:
    'Das Update hat zu lange gedauert und wurde abgebrochen. Bitte Q-Tool näher an das Gerät halten und erneut versuchen.',
  UNKNOWN: 'Unbekannter Fehler. Bitte erneut versuchen.',
};

const errorCopy = computed(() =>
  // The fallback covers a reason from a native build newer than this bundle.
  props.state.phase === 'error'
    ? (ERROR_COPY[props.state.reason] ?? ERROR_COPY.UNKNOWN)
    : ''
);
</script>
